import { DataAccess } from "../../../dataaccess/data.access";
import { RoleRights } from "./role.rights";
import { AccessLevel, AccessLevels } from "../../../pojo/Fls";

/**
 * Static service for role-based rights configuration API calls
 * All methods are static and use DataAccess for HTTP requests
 */
export class RoleRightsService {
    /**
     * Get all right definitions from the backend enum
     * @param category Optional category filter
     */
    public static async getRightDefinitions(category?: string): Promise<RoleRights.RightDefinition[]> {
        const params = category ? `?category=${encodeURIComponent(category)}` : "";
        const response = await DataAccess.get<RoleRights.DefinitionsResponse>(`/role/rights/definitions.json${params}`);
        return response.definitions;
    }

    /**
     * Get all role-right assignments grouped by role
     */
    public static async getAllRoleRights(): Promise<Record<number, RoleRights.RoleRightAssignment[]>> {
        const response = await DataAccess.get<RoleRights.AllRoleRightsResponse>(`/role/rights/all.json`);
        return response.roleRights;
    }

    /**
     * Update which roles have access to a specific right
     * @param rightName The right identifier
     * @param roleIds Array of role IDs that should have this right
     * @param accessLevel The access level to grant (defaults to "F")
     */
    public static async updateRolesForRight(
        rightName: string,
        roleIds: number[],
        accessLevel: AccessLevel = "F" as AccessLevel,
    ): Promise<void> {
        await DataAccess.put(`/role/rights.json`, {
            roleRights: {
                rightName,
                roleIds,
                accessLevel,
            },
        });
    }

    /**
     * Get combined rights configuration with assigned roles
     * Returns rights definitions with their role assignments
     */
    public static async getRightsConfig(): Promise<RoleRights.RightWithRolesConfig[]> {
        return await DataAccess.get<RoleRights.RightWithRolesConfig[]>(`/role/rights/config.json`);
    }

    /**
     * Save roles for a single right
     * @param rightName The right identifier
     * @param roleAssignments Map of roleId to accessLevel
     */
    public static async saveRolesForRight(rightName: string, roleAssignments: Map<number, AccessLevel>): Promise<void> {
        // Extract role IDs that have non-"N" access
        const roleIds: number[] = [];
        let accessLevel: AccessLevel = AccessLevels.Full;

        roleAssignments.forEach((level, roleId) => {
            roleIds.push(roleId);
            accessLevel = level; // All roles get the same access level
        });

        await this.updateRolesForRight(rightName, roleIds, accessLevel);
    }
}
